import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { orpc } from "@/client";
import { errorCode } from "@/lib/error-code";
import { PollCadence } from "@/lib/poll-cadence";
import { TermosLabels } from "@/lib/labels/termos";

function useInvalidate() {
	const queryClient = useQueryClient();

	return () => {
		void queryClient.invalidateQueries({ queryKey: orpc.terms.key() });
	};
}

const TERMINAL_STATES = new Set(["done", "failed"]);

export function useTermList() {
	return useQuery(orpc.terms.list.queryOptions());
}

export function useTermDetail(id: number) {
	return useQuery(
		orpc.terms.get.queryOptions({
			input: { id },
			retry: (_failureCount, error) => errorCode(error) !== "NOT_FOUND",
		}),
	);
}

export function useCustomFieldCreate() {
	const invalidate = useInvalidate();

	return useMutation(orpc.terms.customFields.create.mutationOptions({ onSuccess: invalidate }));
}

export function useCustomFieldUpdate() {
	const invalidate = useInvalidate();

	return useMutation(orpc.terms.customFields.update.mutationOptions({ onSuccess: invalidate }));
}

export function usePatternCreate() {
	const invalidate = useInvalidate();

	return useMutation(orpc.terms.patterns.create.mutationOptions({ onSuccess: invalidate }));
}

export function usePatternUpdate() {
	const invalidate = useInvalidate();

	return useMutation(orpc.terms.patterns.update.mutationOptions({ onSuccess: invalidate }));
}

export function useTermIssueResolve() {
	const invalidate = useInvalidate();

	return useMutation(orpc.terms.review.resolve.mutationOptions({ onSuccess: invalidate }));
}

export function useTermPublish() {
	const invalidate = useInvalidate();

	return useMutation(orpc.terms.review.publish.mutationOptions({ onSuccess: invalidate }));
}

export function useSendRequirements(termIds: number[]) {
	return useQuery(
		orpc.terms.sendRequirements.queryOptions({
			input: { termIds },
			enabled: termIds.length > 0,
		}),
	);
}

export function useTermEdit() {
	const invalidate = useInvalidate();

	return useMutation(
		orpc.terms.edit.mutationOptions({
			onSuccess: () => {
				invalidate();
				toast.success(TermosLabels.editor.savedToast);
			},
			onError: () => toast.error(TermosLabels.editor.saveFailed),
		}),
	);
}

export function useTermImageUpload() {
	return useMutation(
		orpc.terms.images.upload.mutationOptions({
			onError: (error) => toast.error(TermosLabels.image.uploadError(errorCode(error))),
		}),
	);
}

export function useTermRemove() {
	const invalidate = useInvalidate();

	return useMutation(
		orpc.terms.remove.mutationOptions({
			onSuccess: invalidate,
			onError: () => toast.error(TermosLabels.rail.deleteError),
		}),
	);
}

export function useGenerationList(visible: boolean) {
	return useQuery(
		orpc.terms.generation.list.queryOptions({
			refetchInterval: () => PollCadence.pipeline({ visible }),
		}),
	);
}

export function useGenerationStatus(id: number) {
	return useQuery(
		orpc.terms.generation.status.queryOptions({
			input: { id },
			refetchInterval: (query) => {
				const state = query.state.data?.state;

				if (state !== undefined && TERMINAL_STATES.has(state)) {
					return false;
				}

				return 2500;
			},
		}),
	);
}

export function useGenerationCreate() {
	const invalidate = useInvalidate();

	return useMutation(
		orpc.terms.generation.create.mutationOptions({
			onSuccess: invalidate,
			onError: (error) => toast.error(TermosLabels.generation.createError(errorCode(error))),
		}),
	);
}

export function useGenerationCancel() {
	const invalidate = useInvalidate();

	return useMutation(orpc.terms.generation.cancel.mutationOptions({ onSuccess: invalidate }));
}

export function useGenerationRetry() {
	const invalidate = useInvalidate();

	return useMutation(
		orpc.terms.generation.retry.mutationOptions({
			onSuccess: invalidate,
			onError: () => toast.error(TermosLabels.generation.retryError),
		}),
	);
}
